/**
 * PermissionDashboardCard Component
 *
 * Wrapper component for dashboard cards that conditionally renders based on RBAC permissions.
 * Use this to hide dashboard widgets from users who don't have the required permission level.
 *
 * DESIGN DECISION: Dashboard cards are hidden (not shown with placeholder) when user lacks access.
 * This keeps the dashboard clean and only shows relevant information.
 *
 * Usage:
 *   // Hide billing stats card if user doesn't have billing:admin access
 *   <PermissionDashboardCard segment="billing" minLevel="admin">
 *     <GeneralStatistics url={...} />
 *   </PermissionDashboardCard>
 *
 *   // Multiple permission requirements (user needs ALL)
 *   <PermissionDashboardCard segments={[['billing', 'viewer'], ['reports', 'viewer']]}>
 *     <FinancialOverviewCard />
 *   </PermissionDashboardCard>
 *
 * @see docs/projects/rbac/03-design-doc.md Section 9.7 - Dashboard Widget Behavior
 */

import React, { ReactNode } from 'react'
import { usePermissions } from '../../hooks/usePermissions'
import { BusinessSegment, FunctionRole } from '../../types/rbac'

interface PermissionDashboardCardProps {
  /**
   * Single segment to check (use with minLevel)
   */
  segment?: BusinessSegment

  /**
   * Minimum access level required (default: 'viewer')
   */
  minLevel?: FunctionRole

  /**
   * Multiple segment/level pairs - user must have ALL permissions
   * Format: [['segment', 'minLevel'], ...]
   */
  segments?: [BusinessSegment, FunctionRole][]

  /**
   * The dashboard card content to render if permission check passes
   */
  children: ReactNode

  /**
   * Optional: Custom wrapper className (for grid layout purposes)
   * If provided, wraps children in a div with this className when rendered
   */
  wrapperClassName?: string
}

export function PermissionDashboardCard({
  segment,
  minLevel = 'viewer',
  segments,
  children,
  wrapperClassName,
}: PermissionDashboardCardProps): React.ReactElement | null {
  const { hasAccess } = usePermissions()

  let hasPermission: boolean

  if (segments && segments.length > 0) {
    // Multiple segments - user must have ALL permissions
    hasPermission = segments.every(([seg, level]) => hasAccess(seg, level))
  } else if (segment) {
    // Single segment check
    hasPermission = hasAccess(segment, minLevel)
  } else {
    // No segment specified - always show
    hasPermission = true
  }

  // If user doesn't have access, hide the card entirely
  if (!hasPermission) {
    return null
  }

  // If wrapper className provided, wrap in a div
  if (wrapperClassName) {
    return <div className={wrapperClassName}>{children}</div>
  }

  return <>{children}</>
}

export default PermissionDashboardCard
